import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Container, Grid, Typography, styled } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 14,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#0f172a",
  marginBottom: theme.spacing(2.5),
  maxWidth: 560,
  [theme.breakpoints.down("md")]: { fontSize: "1.95rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.65rem" },
}));

const Lead = styled(Typography)(({ theme }) => ({
  fontSize: "1.02rem",
  lineHeight: 1.7,
  color: "rgba(15, 23, 42, 0.7)",
  marginBottom: theme.spacing(2.5),
  maxWidth: 540,
}));

const PrimaryButton = styled(Button)({
  marginTop: 16,
  padding: "12px 24px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  backgroundColor: "#2f81ad",
  color: "#fff",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1d648c", boxShadow: "none" },
  transition: "background-color 0.2s ease",
});

const RailCard = styled(Box)({
  padding: 28,
  borderRadius: 10,
  backgroundColor: "#fff",
  border: "1px solid rgba(15, 23, 42, 0.1)",
});

const RailLabel = styled("div")({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 16,
});

const ModelRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "14px 0",
  borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
  "&:last-of-type": { borderBottom: "none" },
});

const ModelTitle = styled(Typography)({
  fontSize: "0.98rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 4,
});

const ModelCopy = styled(Typography)({
  fontSize: "0.86rem",
  color: "rgba(15, 23, 42, 0.65)",
  lineHeight: 1.55,
});

const ENGAGEMENT_MODELS = [
  {
    title: "Fixed-price project",
    description:
      "Defined scope, milestone-based payments. Best for MVPs and well-scoped builds.",
  },
  {
    title: "Time & materials",
    description:
      "Pay for hours worked. Best for evolving scopes and AI prototyping.",
  },
  {
    title: "Dedicated team",
    description:
      "Embedded squad of senior engineers. Best for long-term product partnerships.",
  },
];

interface EnhancedAboutSectionProps {
  onMoreAboutUs?: () => void;
}

const EnhancedAboutSection: React.FC<EnhancedAboutSectionProps> = ({
  onMoreAboutUs,
}) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.1 }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);

  return (
    <Box
      ref={ref}
      component="section"
      aria-labelledby="about-heading"
      sx={{
        py: { xs: 8, md: 11 },
        backgroundColor: "#fff",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(12px)",
                transition: "opacity 0.5s ease, transform 0.5s ease",
              }}
            >
              <Label>About us</Label>
              <SectionTitle id="about-heading" variant="h2">
                A software team built around senior engineers
              </SectionTitle>
              <Lead>
                Smart IT Innovation is a software and AI consultancy founded in
                2019. Our 20-person team works out of Ontario, Canada and
                Lahore, Pakistan — built to give North American clients
                full-stack delivery without the agency markup.
              </Lead>
              <Lead>
                Whether it's a 6-week MVP, a long-term engineering partnership,
                or an AI feature on top of an existing product, you get the
                same senior team from kickoff to launch.
              </Lead>
              <PrimaryButton
                endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                onClick={onMoreAboutUs}
                disableElevation
              >
                More about us
              </PrimaryButton>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              sx={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(16px)",
                transition: "opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s",
              }}
            >
              <RailCard>
                <RailLabel>Engagement models</RailLabel>
                {ENGAGEMENT_MODELS.map((m) => (
                  <ModelRow key={m.title}>
                    <CheckCircleOutlineIcon
                      sx={{
                        color: "#2f81ad",
                        fontSize: 20,
                        flexShrink: 0,
                        mt: "2px",
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <ModelTitle>{m.title}</ModelTitle>
                      <ModelCopy>{m.description}</ModelCopy>
                    </Box>
                  </ModelRow>
                ))}
              </RailCard>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default EnhancedAboutSection;
